import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Wand2, Store, Mic, MicOff } from "lucide-react";
import { useAiSearch, useProductSearch, useTranscribe } from "../api/hooks";
import { apiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { OfferCard } from "../components/OfferCard";
import { GridSkeleton, EmptyState, ErrorState } from "../components/States";
import { MaskedLine } from "../components/Motion";
import { Seo } from "../components/Seo";
import { ProductCard } from "./PriceComparisonPage";
import { toast } from "../components/ui/sonner";

const HINTS = ["Nike shoes under ₹2000", "Nykaa coupons", "flight deals", "beauty offers"];

const SearchPage = () => {
    const [params, setParams] = useSearchParams();
    const q = params.get("q") || "";
    const [input, setInput] = useState(q);
    const ai = useAiSearch();
    const live = useProductSearch();
    const transcribe = useTranscribe();
    const { user } = useAuth();
    const [recording, setRecording] = useState(false);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);

    // Voice search: record a short clip, transcribe via the app backend, run the query.
    const toggleRecording = async () => {
        if (recording) {
            recorderRef.current?.stop();
            setRecording(false);
            return;
        }
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
            toast.error("Voice search is not supported in this browser.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            chunksRef.current = [];
            recorder.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
            recorder.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
                try {
                    const res = await transcribe.mutateAsync(new File([blob], "voice.webm", { type: blob.type }));
                    const text = (res?.text || "").trim();
                    if (text) {
                        setInput(text);
                        setParams({ q: text });
                    } else {
                        toast.error("Couldn't catch that — please try again.");
                    }
                } catch (err) {
                    toast.error(apiError(err));
                }
            };
            recorder.start();
            recorderRef.current = recorder;
            setRecording(true);
            // safety: auto-stop after 15s
            setTimeout(() => {
                if (recorder.state === "recording") {
                    recorder.stop();
                    setRecording(false);
                }
            }, 15000);
        } catch {
            toast.error("Microphone permission denied.");
        }
    };

    useEffect(() => {
        setInput(q);
        if (q) ai.mutate(q);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    // When the AI understands this as a product query, also pull live store
    // prices (login-gated endpoint) and show them above the deal results.
    useEffect(() => {
        const isProductQuery = ai.data?.interpreted?.intent === "product" || Boolean(ai.data?.interpreted?.product);
        if (user && q && isProductQuery) {
            live.mutate({ query: q });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ai.data, user, q]);

    const submit = (e) => {
        e.preventDefault();
        if (input.trim()) setParams({ q: input.trim() });
    };

    const data = ai.data;
    const liveProducts = live.data?.products || [];

    return (
        <>
            <Seo
                title="AI Search — SahiKhoj"
                description="Ask SahiKhoj AI in plain words and discover matching deals, coupons and offers from supported platforms."
                path="/search"
            />
            <section data-testid="search-hero" className="hero-glow bg-ink pb-16 pt-36 text-cream sm:pb-20 lg:pt-44">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.24em] text-mist">
                        <span className="h-px w-10 bg-saffron" />
                        AI Search
                    </p>
                    <h1 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.06] tracking-tighter sm:text-5xl lg:text-6xl">
                        <MaskedLine delay={0.15}>
                            Ask. <span className="text-saffron">SahiKhoj</span> finds.
                        </MaskedLine>
                    </h1>
                    <form
                        onSubmit={submit}
                        data-testid="ai-search-form"
                        className="mt-9 flex max-w-2xl items-center gap-2 rounded-full border border-cream/15 bg-cream/10 p-2 backdrop-blur-md transition-colors focus-within:border-saffron/60"
                    >
                        <Sparkles className="ml-3 h-5 w-5 shrink-0 text-saffron" />
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            data-testid="ai-search-input"
                            placeholder="Ask SahiKhoj AI..."
                            aria-label="Ask SahiKhoj AI"
                            autoFocus
                            className="w-full bg-transparent text-base text-cream placeholder:text-mist/60 focus:outline-none"
                        />
                        <button
                            type="button"
                            onClick={toggleRecording}
                            data-testid="ai-search-mic-button"
                            aria-label={recording ? "Stop recording" : "Voice search"}
                            aria-pressed={recording}
                            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors ${
                                recording ? "animate-pulse bg-red-500 text-white" : "bg-cream/15 text-cream hover:bg-cream/25"
                            }`}
                        >
                            {recording ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                        </button>
                        <button
                            type="submit"
                            data-testid="ai-search-button"
                            disabled={ai.isPending}
                            className="shrink-0 rounded-full bg-saffron px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-saffron-hover disabled:opacity-60"
                        >
                            {ai.isPending ? "Searching…" : "Search"}
                        </button>
                    </form>
                    <div className="mt-5 flex flex-wrap items-center gap-2">
                        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-mist/70">Try:</span>
                        {HINTS.map((h) => (
                            <button
                                key={h}
                                type="button"
                                onClick={() => setParams({ q: h })}
                                data-testid={`search-hint-${h.split(" ")[0].toLowerCase()}`}
                                className="rounded-full border border-cream/15 px-4 py-1.5 text-xs text-cream/80 transition-colors hover:border-saffron hover:text-saffron"
                            >
                                {h}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-cream py-14 sm:py-20">
                <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
                    {ai.isPending && <GridSkeleton count={8} />}
                    {ai.isError && <ErrorState message={apiError(ai.error)} onRetry={() => q && ai.mutate(q)} />}
                    {!q && !ai.isPending && !data && (
                        <EmptyState
                            title="Search deals in plain words"
                            message="Describe what you are looking for — a brand, a product, a budget — and AI Search will surface matching offers from supported platforms."
                        />
                    )}
                    {data && (
                        <>
                            <div
                                data-testid="ai-search-summary"
                                className="flex flex-wrap items-center gap-3 rounded-3xl border border-ink/5 bg-white p-6 shadow-sm"
                            >
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-saffron-soft">
                                    <Wand2 className="h-5 w-5 text-saffron" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium leading-relaxed text-ink sm:text-base">{data.summary}</p>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {data.interpreted?.brand && (
                                            <span className="rounded-full bg-india/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-india">
                                                Brand: {data.interpreted.brand}
                                            </span>
                                        )}
                                        {data.interpreted?.categoryName && (
                                            <span className="rounded-full bg-india/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-india">
                                                {data.interpreted.categoryName}
                                            </span>
                                        )}
                                        {data.interpreted?.maxPrice != null && (
                                            <span className="rounded-full bg-india/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-india">
                                                Under ₹{data.interpreted.maxPrice}
                                            </span>
                                        )}
                                        <span className="rounded-full bg-saffron-soft px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-saffron">
                                            {data.count} {data.exact ? "matching" : "closest"} result{data.count === 1 ? "" : "s"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {data.interpreted?.priceNote && (
                                <p className="mt-4 text-xs leading-relaxed text-smoke">{data.interpreted.priceNote}</p>
                            )}

                            {user && live.isPending && (
                                <div className="mt-10" data-testid="ai-live-loading">
                                    <div className="h-6 w-48 animate-pulse rounded bg-ink/5" />
                                    <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                                        {[0, 1, 2, 3].map((i) => (
                                            <div key={i} className="animate-pulse rounded-2xl border border-ink/5 bg-white p-5">
                                                <div className="aspect-square w-full rounded-xl bg-ink/5" />
                                                <div className="mt-4 h-4 w-2/3 rounded bg-ink/5" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {user && liveProducts.length > 0 && (
                                <div className="mt-10" data-testid="ai-live-prices">
                                    <h2 className="flex items-center gap-2.5 font-display text-2xl font-semibold tracking-tight text-ink">
                                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-leaf/10">
                                            <Store className="h-5 w-5 text-leaf" />
                                        </span>
                                        Live store prices
                                    </h2>
                                    <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                                        {liveProducts.slice(0, 8).map((p, i) => (
                                            <ProductCard key={`live-${i}`} p={p} isBest={false} />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {user && live.data && liveProducts.length === 0 && live.data.message && (
                                <p className="mt-8 text-xs leading-relaxed text-smoke" data-testid="ai-live-unavailable">
                                    {live.data.message}
                                </p>
                            )}

                            {data.results?.length > 0 ? (
                                <div data-testid="ai-search-results" className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                                    {data.results.map((o) => (
                                        <OfferCard key={o.id} offer={o} />
                                    ))}
                                </div>
                            ) : (
                                <EmptyState title="No matching deals right now" message="Try a different brand, product or category." />
                            )}
                        </>
                    )}
                </div>
            </section>
        </>
    );
};

export default SearchPage;
